<template>
    <div v-show="shouldShowGroup" class="newsletter-group" :class="{ 'newsletter-group__dynamic': group.dynamic }" :data-group-id="group.id">
        <div class="newsletter-group__header"
            @click="toggleExpanded" 
            @keydown.enter="toggleExpanded" 
            @keydown.space.prevent="toggleExpanded"
            role="button" 
            tabindex="0"
            :aria-expanded="isExpanded.toString()"
        >
            <h2 class="newsletter-group__title">{{ group.name.toUpperCase() }}</h2>
            <button
                type="button"
                class="newsletter-group__toggle" 
                :class="{ 'is-expanded': isExpanded }"
                :aria-label="`Toggle ${group.name} newsletters`"
                tabindex="-1"
            >
                <svg width="35" height="35" viewBox="0 0 35 35" fill="none">
                    <path d="M8.75 13.125L17.5 21.875L26.25 13.125" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
            <div class="newsletter-group__separator"></div>
        </div>
        <transition name="expand" @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave">
            <div class="newsletter-group__content" :aria-hidden="! isExpanded" v-show="isExpanded">
                <div v-if="visibleNewsletters.length > 0" class="newsletter-grid">
                    <newsletter-card
                        v-for="newsletter in visibleNewsletters"
                        :key="newsletter.id"
                        :newsletter="newsletter"
                        :initially-selected="newsletter.subscribed || newsletter.preselected"
                        @subscription-changed="handleSubscriptionChange"
                    />
                    <advertisement-card :key="`ad-${group.id}`" :ad="group.ad" v-if="group.ad" />
                </div>
                <p v-else class="newsletter-group__empty">No newsletters available for this region.</p>
            </div>
        </transition>
    </div>
</template>

<script>
import NewsletterCard from './NewsletterCard.vue';
import AdvertisementCard from './AdvertisementCard.vue';

export default {
    name: 'NewsletterGroup',
    components: {
        NewsletterCard,
        AdvertisementCard,
    },
    props: {
        group: {
            type: Object,
            required: true,
            default : () => ({
                id: null,
                name: '',
                filterable: true,
                dynamic: true,
                newsletters: [],
                ad: null,
            }),
        },
        selectedFilters: {
            type: Array,
            default: () => [],
        },
        initiallyExpanded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isExpanded: this.initiallyExpanded,
        };
    },
    mounted() {
        this.resetExpanded();
    },
    computed: {
        hasActiveFilters() {
            return this.selectedFilters.length > 0;
        },
        hasSelectedFilteredNewsletters() {
            return this.selectedFilteredNewsletters.length > 0;
        },
        selectedFilteredNewsletters() {
            return this.group.newsletters.filter((newsletter) => {
                return this.selectedFilters.includes(newsletter.subscription_classification_id) ||
                       this.selectedFilters.includes(newsletter.classification_id);
            });
        },
        visibleNewsletters() {
            if (! this.group.filterable || ! this.hasActiveFilters || ! this.group.newsletters) {
                return (this.group.newsletters || []).slice().sort((a, b) => a.title.localeCompare(b.title));
            }
            var eventsFilterActive = this.selectedFilters.includes(-1);
            return this.group.newsletters.filter((newsletter) => {
                if (newsletter.type === 'Event') {
                    return eventsFilterActive;
                }
                return this.selectedFilters.includes(newsletter.subscription_classification_id) ||
                       this.selectedFilters.includes(newsletter.classification_id);
            }).sort((a, b) => a.title.localeCompare(b.title));
        },
        shouldShowGroup() {
            if (! this.group.filterable || ! this.hasActiveFilters) {
                return true;
            }

            // Show if there are visible newsletters or if it's an ad-only group
            return this.visibleNewsletters.length > 0
                || (this.group.ad && (! this.group.newsletters || this.group.newsletters.length === 0));
        }
    },
    methods: {
        toggleExpanded() {
            this.isExpanded = ! this.isExpanded;
        },
        setExpanded(expanded) {
            this.isExpanded = expanded;
        },
        handleSubscriptionChange(event) {
            this.$emit('subscription-changed', event);
        },
        onEnter(el) {
            el.style.height = '0';
            el.style.overflow = 'hidden';
            el.offsetHeight; // Force reflow
            el.style.transition = 'height 0.3s ease-out';
            el.style.height = el.scrollHeight + 'px';
        },
        onAfterEnter(el) {
            el.style.height = '';
            el.style.overflow = '';
            el.style.transition = '';
        },
        onLeave(el) {
            el.style.height = el.scrollHeight + 'px';
            el.style.overflow = 'hidden';
            el.offsetHeight; // Force a reflow to ensure the browser applies the previous style changes before starting the transition, so the height animation works smoothly.
            el.style.transition = 'height 0.3s ease-out';
            el.style.height = '0';
        },
        resetExpanded() {
            if (! this.hasActiveFilters) {
                this.isExpanded = this.initiallyExpanded;
            } else if (this.group.dynamic) {
                this.isExpanded = false;
            } else {
                this.isExpanded = this.hasSelectedFilteredNewsletters;
            }
        },
    },
};
</script>

<style scoped>
.newsletter-group {
    margin-bottom: 40px;
}

.newsletter-group__header {
    position: relative;
    display: flex;
    align-items: center;
    cursor: pointer;
    padding-bottom: 15px;
}

.newsletter-group__title {
    font-family: 'Oswald', sans-serif;
    font-weight: 500;
    font-size: 27px;
    color: black;
    text-transform: uppercase;
    margin: 0;
    flex: 1;
}

.newsletter-group__dynamic .newsletter-group__title {
    color: #ef8125;
}

.newsletter-group__toggle {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: transform 0.3s ease;
    color: #999;
}

.newsletter-group__toggle.is-expanded {
    transform: rotate(180deg);
}

.newsletter-group__separator {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(to right, #ddd 0%, transparent 100%);
}

.newsletter-group__content {
    padding-top: 30px;
}

.newsletter-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 30px;
    width: 100%;
}

.newsletter-group__empty {
    color: #666;
    font-style: italic;
    text-align: center;
    padding: 40px;
}

@media (max-width: 1024px) {
    .newsletter-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 768px) {
    .newsletter-group__title {
        font-size: 24px;
    }
    
    .newsletter-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
    }
}

@media (max-width: 480px) {
    .newsletter-grid {
        grid-template-columns: 1fr;
    }
}
</style>